import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import ClientsMenu from '@/components/ClientsMenu';

const Navigation = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isClientsMenuOpen, setIsClientsMenuOpen] = useState(false);
  const clientsRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Throttle scroll handler for better performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    // { name: 'Product', href: '#product' },
    { 
      name: 'Vectoral System', 
      href: '#vectoral',
      highlight: true // Mark as important
    },
    { name: 'Clients', href: '#vectoral', hasMegaMenu: true, menuType: 'clients' },
    // { name: 'Research', href: '#research' },
    // { name: 'Core Team', href: '/team', isRoute: true },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href: string) => {
    // If we're on a different route, navigate to home first, then scroll
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (link: { href: string; isRoute?: boolean }) => {
    if (link.isRoute) {
      navigate(link.href);
      setIsMobileMenuOpen(false);
    } else {
      scrollToSection(link.href);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-expo-out ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm'
            : 'bg-white/95 backdrop-blur-xl'
        }`}
      >
        <div className="relative">
          {/* Logo - positioned at absolute left */}
          <Link
            to="/"
            className="absolute left-0 top-0 h-20 flex items-center pl-4 sm:pl-6 lg:pl-8 z-10"
            style={{ background: 'none' }}
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <img 
              src="/kmg-logo.png" 
              alt="KMG Logo" 
              className="h-8 sm:h-10 w-auto object-contain"
              style={{ 
                backgroundColor: 'transparent',
                background: 'none',
                display: 'block'
              }}
            />
          </Link>

          {/* Navigation content - centered with space for logo */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-20">
              {/* Spacer to balance logo on left */}
              <div className="w-32 sm:w-40 lg:w-48"></div>

              {/* Desktop Navigation - centered */}
              <div className="hidden md:flex items-center space-x-8 flex-1 justify-center">
              {navLinks.map((link) => {
                if (link.hasMegaMenu && link.menuType === 'clients') {
                  return (
                    <div 
                      key={link.name} 
                      className="relative flex items-center"
                      onMouseEnter={() => setIsClientsMenuOpen(true)}
                      onMouseLeave={() => setIsClientsMenuOpen(false)}
                    >
                      <button
                        ref={clientsRef}
                        onClick={() => scrollToSection(link.href)}
                        className="relative py-1.5 text-sm font-medium transition-colors duration-300 group text-slate-600 hover:text-kmg-primary"
                      >
                        {link.name}
                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-kmg-primary transition-all duration-300 ease-expo-out group-hover:w-full" />
                      </button>
                      <div className="absolute top-full left-0">
                        <ClientsMenu 
                          isOpen={isClientsMenuOpen} 
                          onClose={() => setIsClientsMenuOpen(false)}
                          triggerRef={clientsRef}
                        />
                      </div>
                    </div>
                  );
                }
                return (
                  <button
                    key={link.name}
                    onClick={() => handleNavClick(link)}
                    className={`relative py-1.5 text-sm font-medium transition-colors duration-300 group ${
                      link.highlight 
                        ? 'text-kmg-primary font-semibold px-3 rounded-lg bg-kmg-primary/10 hover:bg-kmg-primary/20' 
                        : 'text-slate-600 hover:text-kmg-primary'
                    }`}
                  >
                    {link.name}
                    {!link.highlight && (
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-kmg-primary transition-all duration-300 ease-expo-out group-hover:w-full" />
                    )}
                  </button>
                );
              })}
              </div>

              {/* CTA Button */}
              <div className="hidden md:block">
                <button
                  onClick={() => scrollToSection('#contact')}
                  className="px-6 py-2.5 text-sm font-semibold text-white bg-kmg-primary rounded-full hover:bg-kmg-primary-dark hover:shadow-glow transition-all duration-300 ease-expo-out hover:scale-105"
                >
                  Get Started
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 text-slate-700 relative z-[60] cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsMobileMenuOpen((prev) => !prev);
                }}
                aria-label="Toggle mobile menu"
                type="button"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-white md:hidden"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            zIndex: 9999,
            overflow: 'auto',
          }}
          onClick={(e) => {
            // Close menu when clicking outside the menu content
            if (e.target === e.currentTarget) {
              setIsMobileMenuOpen(false);
            }
          }}
        >
          {/* Close button in mobile menu */}
          <button
            className="absolute top-4 right-4 p-3 text-slate-700 bg-white rounded-full shadow-lg border border-slate-200"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsMobileMenuOpen(false);
            }}
            aria-label="Close mobile menu"
            type="button"
          >
            <X size={24} />
          </button>
        
          <div 
            className="flex flex-col items-center justify-center space-y-8 px-4 w-full"
            style={{ 
              minHeight: '100vh',
              paddingTop: '80px',
              paddingBottom: '40px',
            }}
          >
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNavClick(link);
                }}
                className="text-2xl font-semibold text-slate-900 hover:text-kmg-primary transition-colors duration-300 py-2 w-full text-center"
                type="button"
              >
                {link.name}
              </button>
            ))}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                scrollToSection('#contact');
              }}
              className="mt-8 px-8 py-3 text-lg font-semibold text-white bg-kmg-primary rounded-full hover:bg-kmg-primary-dark transition-colors"
              type="button"
            >
              Get Started
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
